// Global voice-over singleton. Ensures only ONE voice clip plays at a time
// (welcome and farewell can never overlap) and exposes a `playing` flag so
// React components can render a blocking overlay while any clip is active.

import { useEffect, useState } from 'react';

const state = {
  current: null,
  playing: false,
  listeners: new Set(),
};

function notify() {
  for (const fn of state.listeners) {
    try { fn(state.playing); } catch (e) { console.debug('voiceOver listener error:', e); }
  }
}

function subscribe(fn) {
  state.listeners.add(fn);
  return () => state.listeners.delete(fn);
}

/**
 * Play a voice-over clip.
 *  - If another clip is already playing, the new request is IGNORED (never stacks).
 *  - Fires `onEnd` when playback completes (or on error / safety timeout).
 *  - Returns the underlying Audio element (or null if skipped).
 *
 * Autoplay policy: browsers block audio.play() without a prior user gesture
 * (typical on iOS Safari and desktop Chrome when landing on a URL directly
 * instead of clicking a link). When that happens we install a one-shot
 * pointerdown/touchstart/click listener that retries play() on the very
 * next user interaction. The listener self-removes after firing.
 */
export function playVoiceOver(url, { onEnd, volume = 0.9, safetyTimeoutMs = 15000, retryOnGesture = true } = {}) {
  if (state.playing) {
    // Skip — never stack voice-overs on top of each other.
    onEnd && setTimeout(onEnd, 0);
    return null;
  }
  let audio;
  try {
    audio = new Audio(url);
    audio.volume = volume;
  } catch (e) {
    console.debug('voiceOver: Audio() failed', e);
    onEnd && setTimeout(onEnd, 0);
    return null;
  }
  state.current = audio;
  state.playing = true;
  notify();

  let done = false;
  let gestureCleanup = null;
  const removeGestureListener = () => {
    if (gestureCleanup) { gestureCleanup(); gestureCleanup = null; }
  };
  const finish = () => {
    if (done) return;
    done = true;
    removeGestureListener();
    if (state.current === audio) {
      state.current = null;
      state.playing = false;
      notify();
    }
    onEnd && onEnd();
  };
  audio.addEventListener('ended', finish);
  audio.addEventListener('error', finish);

  const armGestureRetry = () => {
    if (!retryOnGesture) return;
    const handler = () => {
      removeGestureListener();
      if (done) return;
      // Re-arm playing state — the clip is about to start for real
      state.current = audio;
      state.playing = true;
      notify();
      audio.play().catch(() => finish());
    };
    document.addEventListener('pointerdown', handler, { once: true, passive: true });
    document.addEventListener('touchstart', handler, { once: true, passive: true });
    document.addEventListener('click', handler, { once: true });
    gestureCleanup = () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('click', handler);
    };
  };

  audio.play().then(() => {
    // Successfully started — arm the safety fallback in case ended/error never fires.
    setTimeout(finish, safetyTimeoutMs);
  }).catch((err) => {
    // NotAllowedError / autoplay policy → wait for user gesture and retry.
    // Any other error (missing file, decode failure) → finish immediately.
    const isAutoplayBlock = err?.name === 'NotAllowedError' || /gesture|interact/i.test(err?.message || '');
    if (isAutoplayBlock && retryOnGesture) {
      // eslint-disable-next-line no-console
      console.log('[voiceOver] play() blocked by autoplay policy — will retry on next user gesture');
      // Roll back the "playing" flag so the UI doesn't show the blocker
      // overlay while we wait for the gesture. state.current is intentionally
      // cleared too so a NEW playVoiceOver() call can still preempt this one
      // (e.g. user leaves the group before welcome ever plays → farewell wins).
      state.current = null;
      state.playing = false;
      notify();
      armGestureRetry();
      // Safety: if no gesture within 30s, silently give up.
      setTimeout(() => {
        if (done) return;
        done = true;
        removeGestureListener();
        onEnd && onEnd();
      }, 30000);
      return;
    }
    finish();
  });
  return audio;
}

/** Whether a voice-over is currently playing (module-level, sync). */
export function isVoiceOverPlaying() {
  return state.playing;
}

/** React hook: re-renders when the voice-over playing state changes. */
export function useVoiceOverPlaying() {
  const [playing, setPlaying] = useState(state.playing);
  useEffect(() => subscribe(setPlaying), []);
  return playing;
}
